import { Link } from 'react-router'
import { BackLink } from '#app/components/back-link.tsx'
import { PageFrame, ReadingColumn } from '#app/components/page-frame.tsx'
import { DemoCapsule } from '#app/features/portfolio/components/demo-capsule.tsx'
import { type Route } from './+types/writing.pipeline.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'Multi-modal ingestion pipeline · slvr.mn' },
	{
		name: 'description',
		content:
			'Local-model ingestion of unstructured financial evidence — MLX Whisper, Qwen3-VL and OCR over statements, PDFs and screen recordings, content-hashed and full-text searchable, out as structured sourced entries.',
	},
]

// Each stage is a real step in the pipeline, not a marketing abstraction. If you
// edit these, keep them consistent with DATA.summaryMore and the skills chips —
// the multi-modal claims appear in three places and drifted once already.
const stages = [
	{
		n: '01',
		title: 'Ingest',
		body: 'Exchange statements, bank PDFs, screenshots, and screen recordings — the records that never touched a chain. Each artifact is content-hashed on the way in, so re-importing the same source is a no-op rather than a duplicate.',
	},
	{
		n: '02',
		title: 'Transcribe',
		body: 'Audio and video through MLX Whisper, running on-device. Timestamps are retained per segment, so a figure lifted from a recording can point back at the second it was spoken.',
	},
	{
		n: '03',
		title: 'Read',
		body: 'Qwen3-VL for vision and document layout, OCR for the rest. Statements and screenshots become text with their structure intact — tables stay tables, columns stay columns.',
	},
	{
		n: '04',
		title: 'Index',
		body: 'Everything lands in a full-text searchable corpus (SQLite FTS5, BM25-ranked) covering 100GB+ of source, with the path and page of every hit preserved.',
	},
	{
		n: '05',
		title: 'Structure',
		body: 'The model reads the corpus and proposes ledger entries; the deterministic engine decides whether they stand. Every extracted figure carries a pointer back to the page, frame, or timestamp it came from.',
	},
]

export default function PipelinePage() {
	return (
		<PageFrame>
			<ReadingColumn>
				<BackLink fallback={{ to: '/writing', label: 'Writing' }} />
				<h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
					Multi-modal ingestion — local models, structured out
				</h1>
				<DemoCapsule href="/writing/pipeline" />
				<p className="text-muted-foreground mt-4 text-pretty">
					When FTX collapsed the proof lived off-chain: funds frozen on an
					exchange that never touched a wallet. No amount of on-chain
					reconstruction reaches that money — the only record is a statement, a
					screenshot, a screen recording. This pipeline is what turns those into
					entries a ledger can book, and it runs entirely on local models, which
					matters when the input is somebody's complete financial history.
				</p>

				{/* Diagram slot — see issue 32 in the planning repo. The harness diagram's
				    responsive layout is being rebuilt; this pipeline diagram should reuse
				    whatever pattern lands there rather than inventing a second one. Nothing
				    is rendered until then: an empty slot is not visitor-facing content. */}

				<h2 className="mt-10 text-xl font-semibold tracking-tight">
					The stages
				</h2>
				<div className="mt-4 flex flex-col gap-3">
					{stages.map((s) => (
						<div key={s.n} className="rounded-lg border p-4">
							<div className="flex items-baseline gap-3">
								<span className="text-muted-foreground text-xs tabular-nums">
									{s.n}
								</span>
								<h3 className="text-sm font-semibold">{s.title}</h3>
							</div>
							<p className="text-muted-foreground mt-1 text-sm text-pretty">
								{s.body}
							</p>
						</div>
					))}
				</div>

				<h2 className="mt-10 text-xl font-semibold tracking-tight">
					Why local
				</h2>
				<p className="text-muted-foreground mt-3 text-pretty">
					The input is a person's complete financial record — account numbers,
					balances, addresses, and in the reconciliation work, somebody else's.
					Sending that to a hosted model is a disclosure decision, not a
					technical one, and the answer is no. Running Whisper and Qwen3-VL
					on-device removes the question: nothing leaves the machine, there is
					no retention policy to read, and the cost of reprocessing a 100GB
					corpus is electricity rather than per-token billing. It also makes the
					pipeline reproducible — the same inputs and the same model weights
					give the same output a year later.
				</p>

				<p className="text-muted-foreground mt-6 text-sm">
					What happens to these entries once they exist — and why a model is
					allowed to propose them but never to write them —{' '}
					<Link
						to="/writing/harness"
						className="text-foreground underline underline-offset-4"
					>
						is the harness architecture
					</Link>
					.
				</p>
			</ReadingColumn>
		</PageFrame>
	)
}
